import React, { useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Spinner from '../components/Spinner';

const LoginPage = lazy(() => import('../pages/Login/LoginPage'));
const ChangePasswordPage = lazy(() => import('../pages/Login/ChangePasswordPage'));
const HomePage = lazy(() => import('../pages/Home/HomePage'));
const ToolsListPage = lazy(() => import('../pages/Tools/ToolsListPage'));
const ToolFormPage = lazy(() => import('../pages/Tools/ToolFormPage'));
const ToolEditPage = lazy(() => import('../pages/Tools/ToolEditPage'));
const ToolDetailsPage = lazy(() => import('../pages/Tools/ToolDetailsPage'));
const ToolHistoryPage = lazy(() => import('../pages/Tools/ToolHistoryPage'));
const PartnersListPage = lazy(() => import('../pages/Partners/PartnersListPage'));
const PartnerFormPage = lazy(() => import('../pages/Partners/PartnerFormPage'));
const PartnerEditPage = lazy(() => import('../pages/Partners/PartnerEditPage'));
const PartnerDetailsPage = lazy(() => import('../pages/Partners/PartnerDetailsPage'));
const UserAdminPage = lazy(() => import('../pages/Admin/UserAdminPage'));
const SolicitationListPage = lazy(() => import('../pages/Solicitations/SolicitationListPage'));
const SolicitationFormPage = lazy(() => import('../pages/Solicitations/SolicitationFormPage'));
const SolicitationDetailsPage = lazy(() => import('../pages/Solicitations/SolicitationDetailsPage'));
const CommercialDashboardPage = lazy(() => import('../pages/Commercial/CommercialDashboardPage'));
const CommissionUploadPage = lazy(() => import('../pages/Comissions/CommissionUploadPage'));
const CommissionListPage = lazy(() => import('../pages/Comissions/CommissionListPage'));
const CorrectionQueuePage = lazy(() => import('../pages/Comissions/CorrectionQueuePage'));
const CorporateUploadPage = lazy(() => import('../pages/corporate/CorporateUploadPage'));
const CorporateListPage = lazy(() => import('../pages/corporate/CorporateListPage'));
const RotasPage = lazy(() => import('../pages/Rotas/RotasPage'));
const RotaUploadPage = lazy(() => import('../pages/Rotas/RotaUploadPage'));
const RotaDetailsPage = lazy(() => import('../pages/Rotas/RotaDetailsPage'));
const RotaEditPage = lazy(() => import('../pages/Rotas/RotaEditPage'));


const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
    const { user } = useAuth();
    const isAllowed = user?.department?.toLowerCase() === 'administrativo';
    return isAllowed ? children : <Navigate to="/" replace />;
};

const MainLayout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    const handleToggleCollapse = () => {
        setIsAnimating(true);
        setSidebarCollapsed(prevState => !prevState);
        setTimeout(() => {
            setIsAnimating(false);
            window.dispatchEvent(new Event('resize'));
        }, 350);
    };

    return (
        <div className="flex h-screen bg-base-background text-gray-800">
            <Sidebar
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              sidebarCollapsed={sidebarCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header setSidebarOpen={setSidebarOpen} />
                <main className="flex-1 overflow-x-hidden overflow-y-auto p-4 md:p-8">
                    {React.cloneElement(children, { isSidebarAnimating: isAnimating })}
                </main>
            </div>
        </div>
    );
};


function AppRouter() {
  const { isAuthenticated, mustChangePassword } = useAuth();

  return (
    <Suspense fallback={<div className="w-full h-screen flex items-center justify-center"><Spinner color="primary" /></div>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {isAuthenticated && mustChangePassword ? (
          <>
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="*" element={<Navigate to="/change-password" replace />} />
          </>
        ) : (
          <Route path="/*" element={
            <ProtectedRoute>
              <Routes>
                  <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
                  <Route path="/tools" element={<MainLayout><ToolsListPage /></MainLayout>} />
                  <Route path="/tool-form" element={<MainLayout><ToolFormPage /></MainLayout>} />
                  <Route path="/tool-edit/:id" element={<MainLayout><ToolEditPage /></MainLayout>} />
                  <Route path="/tool-details/:id" element={<MainLayout><ToolDetailsPage /></MainLayout>} />
                  <Route path="/tool-history/:id" element={<MainLayout><ToolHistoryPage /></MainLayout>} />
                  <Route path="/partners" element={<MainLayout><PartnersListPage /></MainLayout>} />
                  <Route path="/partner-form" element={<MainLayout><PartnerFormPage /></MainLayout>} />
                  <Route path="/partner-edit/:id" element={<MainLayout><PartnerEditPage /></MainLayout>} />
                  <Route path="/partner-details/:id" element={<MainLayout><PartnerDetailsPage /></MainLayout>} />
                  <Route path="/solicitations" element={<MainLayout><SolicitationListPage /></MainLayout>} />
                  <Route path="/solicitation-form" element={<MainLayout><SolicitationFormPage /></MainLayout>} />
                  <Route path="/solicitation-details/:id" element={<MainLayout><SolicitationDetailsPage /></MainLayout>} />
                  <Route path="/admin" element={<AdminRoute><MainLayout><UserAdminPage /></MainLayout></AdminRoute>} />
                  <Route path="/commercial-dashboard" element={<MainLayout><CommercialDashboardPage /></MainLayout>} />
                  <Route path="/comissions/upload" element={<MainLayout><CommissionUploadPage /></MainLayout>} />
                  <Route path="/comissions/list" element={<MainLayout><CommissionListPage /></MainLayout>} />
                  <Route path="/comissions/correction-queue" element={<MainLayout><CorrectionQueuePage /></MainLayout>} />
                  <Route path="/corporate/upload" element={<MainLayout><CorporateUploadPage /></MainLayout>} />
                  <Route path="/corporate/reports" element={<MainLayout><CorporateListPage /></MainLayout>} />
                  <Route path="/rotas" element={<MainLayout><RotasPage /></MainLayout>} />
                  <Route path="/rotas/upload" element={<MainLayout><RotaUploadPage /></MainLayout>} />
                  <Route path="/rotas/details/:id" element={<MainLayout><RotaDetailsPage /></MainLayout>} />
                  <Route path="/rotas/edit/:id" element={<MainLayout><RotaEditPage /></MainLayout>} />
                  <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </ProtectedRoute>
          }/>
        )}
      </Routes>
    </Suspense>
  );
}
export default AppRouter;